import React from 'react';
import {
	Box,
	Collapse,
	Grid,
	List,
	ListItem,
	ListItemIcon,
	ListItemText,
	Radio,
	TextField,
} from '@mui/material';
import { BillingAddress } from 'common/modules/billing';
import AutoCompleteCountryPicker from 'common/components/pickers/AutoCompleteCountryPicker';
import { Address } from 'common/modules/atoms/addresses';
import { useSelector } from 'react-redux';
import { useTranslation } from 'services/localization';
import * as ShopSelectors from 'selectors/ShopSelectors';

interface Props {
	value: BillingAddress;
	onChange: (value: BillingAddress) => void;
}
const BillingAddressForm = (props: Props) => {
	const { t } = useTranslation();
	const { value, onChange } = props;

	const shopPublicInfo = useSelector(ShopSelectors.activeShopPublicInfo);

	const handleTypeChange = (type: BillingAddress['type']) => () => {
		if (type === 'custom') {
			onChange({
				type: 'custom',
				address: {
					address: '',
					city: '',
					zipCode: '',
					state: '',
					country: '',
				},
			});
		} else {
			onChange({
				type: 'default',
			});
		}
	};

	const handleAddressFieldChange = (field: keyof Address) => (
		e: React.ChangeEvent<HTMLInputElement>,
	) => {
		if (value.type === 'custom') {
			onChange({
				type: 'custom',
				address: {
					...value.address,
					[field]: e.target.value,
				},
			});
		}
	};

	const handleAddressCountryChange = (newCountry: string) => {
		if (value.type === 'custom') {
			onChange({
				type: 'custom',
				address: {
					...value.address,
					country: newCountry,
				},
			});
		}
	};

	const getAddressFieldValue = (field: keyof Address) => {
		return value.type === 'custom' ? value.address[field] : '';
	};

	return (
		<>
			<List>
				<ListItem button onClick={handleTypeChange('default')}>
					<ListItemIcon>
						<Radio checked={value.type === 'default'} color="primary" />
					</ListItemIcon>
					<ListItemText
						primary={t('BillingInformation.storeAddress', 'Store address')}
						secondary={t(
							'BillingInformation.storeAddressHelper',
							'Use the street address of the store which the invoice is for',
						)}
					/>
				</ListItem>
				<ListItem button onClick={handleTypeChange('custom')}>
					<ListItemIcon>
						<Radio checked={value.type === 'custom'} color="primary" />
					</ListItemIcon>
					<ListItemText primary={t('BillingInformation.customAddress', 'Custom billing address')} />
				</ListItem>
			</List>
			<Collapse in={value.type === 'custom'}>
				<Box ml={9}>
					<Grid container spacing={2}>
						<Grid item xs={12}>
							<TextField
								label={t('common:form.streetAddress', 'Street address')}
								placeholder="425 Grove Street"
								value={getAddressFieldValue('address')}
								onChange={handleAddressFieldChange('address')}
								fullWidth
								variant="outlined"
							/>
						</Grid>
						<Grid item xs={12} md={6}>
							<TextField
								label={t('common:form.city', 'City')}
								placeholder="New York"
								value={getAddressFieldValue('city')}
								onChange={handleAddressFieldChange('city')}
								fullWidth
								variant="outlined"
							/>
						</Grid>
						<Grid item xs={12} md={6}>
							<TextField
								label={t('common:form.postalCode', 'Postal code')}
								placeholder="10001"
								value={getAddressFieldValue('zipCode')}
								onChange={handleAddressFieldChange('zipCode')}
								fullWidth
								variant="outlined"
							/>
						</Grid>
						<Grid item xs={12}>
							<AutoCompleteCountryPicker
								label={t('common:address.country')}
								id="country-select"
								value={(getAddressFieldValue('country') || shopPublicInfo?.country) ?? ''}
								onChange={handleAddressCountryChange}
								variant="outlined"
							/>
						</Grid>
					</Grid>
				</Box>
			</Collapse>
		</>
	);
};

export default BillingAddressForm;
